import { Input, Textarea, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { CharacterExpertise } from '@creanizer/types'

interface ExpertiseEditorProps {
  expertise: CharacterExpertise
  onChange: (expertise: CharacterExpertise) => void
}

export function ExpertiseEditor({
  expertise,
  onChange
}: ExpertiseEditorProps) {
  const [newSkill, setNewSkill] = useState('')
  const [newEpisode, setNewEpisode] = useState('')

  const handleAddSkill = () => {
    if (newSkill) {
      onChange({
        ...expertise,
        practicalSkills: [...(expertise.practicalSkills || []), newSkill]
      })
      setNewSkill('')
    }
  }

  const handleRemoveSkill = (index: number) => {
    onChange({
      ...expertise,
      practicalSkills: (expertise.practicalSkills || []).filter((_, i) => i !== index)
    })
  }

  const handleAddEpisode = () => {
    if (newEpisode) {
      onChange({
        ...expertise,
        characteristicEpisodes: [...(expertise.characteristicEpisodes || []), newEpisode]
      })
      setNewEpisode('')
    }
  }

  const handleRemoveEpisode = (index: number) => {
    onChange({
      ...expertise,
      characteristicEpisodes: (expertise.characteristicEpisodes || []).filter((_, i) => i !== index)
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="mainField">主な専門分野</Label>
        <Textarea
          id="mainField"
          value={expertise.mainField || ''}
          onChange={(e) => onChange({ ...expertise, mainField: e.target.value })}
          placeholder="例: 薬学、剣術、交渉術"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">実務能力・技能</Label>
        <div className="space-y-2 mt-2">
          {(expertise.practicalSkills || []).map((skill, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={skill}
                onChange={(e) => {
                  const updated = [...(expertise.practicalSkills || [])]
                  updated[index] = e.target.value
                  onChange({ ...expertise, practicalSkills: updated })
                }}
                className="flex-1"
              />
              <Button size="sm" variant="ghost" onClick={() => handleRemoveSkill(index)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              placeholder="例: 料理、応急処置、馬術"
              onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
              className="flex-1"
            />
            <Button size="sm" onClick={handleAddSkill}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="intellectualWorks">著書・知的活動</Label>
        <Textarea
          id="intellectualWorks"
          value={expertise.intellectualWorks || ''}
          onChange={(e) => onChange({ ...expertise, intellectualWorks: e.target.value })}
          placeholder="例: 魔法理論の研究書を執筆、詩集を出版"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">キャラクター性を示すエピソード</Label>
        <div className="space-y-2 mt-2">
          {(expertise.characteristicEpisodes || []).map((episode, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={episode}
                onChange={(e) => {
                  const updated = [...(expertise.characteristicEpisodes || [])]
                  updated[index] = e.target.value
                  onChange({ ...expertise, characteristicEpisodes: updated })
                }}
                className="flex-1"
              />
              <Button size="sm" variant="ghost" onClick={() => handleRemoveEpisode(index)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newEpisode}
              onChange={(e) => setNewEpisode(e.target.value)}
              placeholder="例: 一晩で薬を調合して村を救った"
              onKeyPress={(e) => e.key === 'Enter' && handleAddEpisode()}
              className="flex-1"
            />
            <Button size="sm" onClick={handleAddEpisode}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
